import { HorizBox, HoverView, Pad, PadBox } from 'component/basics';
import { colorBorderPrimary, colorButtonAccent, colorSurfacePrimary, colorTagAccentLight } from 'component/color';
import { UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { StyleSheet, View } from 'react-native';
import type { TLabel } from 'util/stdtypes';
import { makeTestID } from 'util/stdtypes';
import { translucentHoverColor, translucentPressColor } from 'util/util-color';

export type ToggleSize = 'regular' | 'compact';

export type REPLACE_ZDF_ToggleProps = {
    emoji?: string;
    text?: string;
    label?: TLabel;
    value?: boolean;
    spread?: boolean;
    onChange?: (value: boolean) => void;
    testID?: string;
    disabled?: boolean;
    size?: ToggleSize;
};

/** Extended version of the `Toggle` button
 * @param disabled Optional disabled state to prevent pointer events and keyboard navigation via tab. The disabled state has no visual impact.
 * @param size Allows choosing between a regular version and a compact version of the toggle button with vertical padding removed
 */
export function REPLACE_ZDF_Toggle({
    emoji,
    text,
    label,
    value = false,
    spread = false,
    onChange,
    testID,
    disabled = false,
    size = 'regular' as ToggleSize,
}: REPLACE_ZDF_ToggleProps) {
    const s = ToggleStyle;
    const tLabel = useTranslation(label);
    return (
        <HoverView
            ariaText={value ? `${text ?? tLabel}, checked` : `${text ?? tLabel}, unchecked`}
            disabled={disabled}
            hoverStyle={s.hover}
            onPress={() => onChange?.(!value)}
            role='button'
            shrink
            style={s.outer}
            testID={makeTestID({ testID, label, text })}
        >
            <PadBox vert={size === 'compact' ? 0 : 8}>
                <HorizBox center shrink spread={spread}>
                    <HorizBox center shrink>
                        {emoji && (
                            <PadBox right={8}>
                                <UtilityText aria-hidden text={emoji} type='tiny' weight='strong' />
                            </PadBox>
                        )}
                        <UtilityText label={label} text={text} />
                    </HorizBox>
                    <Pad size={12} />
                    <View style={value ? s.toggleZoneSelected : s.toggleZone}>
                        <View style={value ? s.toggleBallSelected : s.toggleBall} />
                    </View>
                </HorizBox>
            </PadBox>
        </HoverView>
    );
}

const ToggleStyle = StyleSheet.create({
    outer: {
        overflow: 'hidden',
    },
    toggleZone: {
        width: 56,
        height: 32,
        backgroundColor: colorBorderPrimary,
        borderRadius: 100,
        ...({ transition: 'background-color 0.2s ease-in-out' } as any),
    },
    toggleBall: {
        boxShadow: '2px 0px 10px rgba(0, 0, 0, 0.30)' as any, // FIXME: boxShadow not typed for native
        elevation: 5, // for Android,
        position: 'absolute',
        left: 2,
        top: 2,
        width: 28,
        height: 28,
        borderRadius: 100,
        backgroundColor: colorSurfacePrimary,
        ...({ transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out' } as any),
    },
    toggleZoneSelected: {
        width: 56,
        height: 32,
        backgroundColor: colorTagAccentLight,
        borderRadius: 100,
        ...({ transition: 'background-color 0.2s ease-in-out' } as any),
    },
    toggleBallSelected: {
        backgroundColor: colorButtonAccent,
        left: 56 - 28 - 2,
        top: 2,
        width: 28,
        height: 28,
        borderRadius: 100,
        ...({ transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out' } as any),
    },
    hover: {
        backgroundColor: translucentHoverColor(),
    },
    pressed: {
        backgroundColor: translucentPressColor(),
        borderTopRightRadius: 100,
        borderBottomRightRadius: 100,
    },
});
